import React, { useEffect } from 'react';
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Checkbox,
  Empty,
  Segmented,
  Select,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import {
  ensureInspectAssessmentCandidatePositions,
  fetchInspectAssessmentInstruments,
  fetchInspectAssessmentKlasses,
  fetchInspectAssessmentOptimalPortfolio,
  fetchInspectAssessmentPeriodBatch,
  fetchInspectAssessmentPeriodNews,
  fetchInspectAssessmentPeriodPrices,
  fetchInspectAssessmentPeriods,
  fetchInspectAssessmentSliderDirection,
  fetchInspectAssessmentUserAssignments,
  fetchInspectAssessmentUsers,
  fetchInspectAssessments,
  inspectAssessmentPeriodPriceKey,
  inspectAssessmentCandidatePositionsRequestKey,
  inspectAssessmentKlassesRequestKey,
  inspectAssessmentOptimalPortfolioRequestKey,
  inspectAssessmentSliderDirectionRequestKey,
  inspectAssessmentUserAssignmentsContextKey,
  inspectAssessmentUserAssignmentsRequestKey,
  inspectAssessmentUsersRequestKey,
  inspectAssessmentsRequestKey,
  selectInspectAssessment,
  selectInspectAssessmentInstrument,
  selectInspectAssessmentKlass,
  selectInspectAssessmentPeriod,
  selectInspectAssessmentState,
  selectInspectAssessmentUser,
  selectInspectAssessmentUserSession,
} from '../../../../state/app/inspect_assessment/actions';
import type {
  InspectAssessmentKlassesRequest,
  InspectAssessmentPeriodsBatchRequest,
  InspectAssessmentOptimalPortfolioRequest,
  InspectAssessmentPillarOnePeriod,
  InspectAssessmentSliderDirectionRequest,
  InspectAssessmentUserAssignment,
  InspectAssessmentUserAttempt,
  InspectAssessmentUsersRequest,
  InspectAssessmentsRequest,
} from '../../../../state/app/inspect_assessment/types';
import FullAssessmentWorkspace from './FullAssessmentWorkspace';
import InstrumentSidecar from './InstrumentSidecar';
import InstrumentPriceChart from './InstrumentPriceChart';
import InstrumentRiskLimitsTable from './InstrumentRiskLimitsTable';
import OptimalPortfolioWorkspace from './OptimalPortfolioWorkspace';
import OverallPortfolioFitWorkspace from './OverallPortfolioFitWorkspace';
import PeriodSidecar from './PeriodSidecar';
import PillarOneComponentTabs from './PillarOneComponentTabs';
import PillarOneRestraintWorkspace from './PillarOneRestraintWorkspace';
import PillarOneScoreGuide from './PillarOneScoreGuide';
import PillarOneScoreWorkspace from './PillarOneScoreWorkspace';
import PillarOneSliderDirectionWorkspace from './PillarOneSliderDirectionWorkspace';
import PillarOneSliderMagnitudeWorkspace from './PillarOneSliderMagnitudeWorkspace';
import PillarThreeComponentTabs from './PillarThreeComponentTabs';
import PillarTwoComponentTabs from './PillarTwoComponentTabs';
import AssessmentUserSidecar from './AssessmentUserSidecar';
import UserNewsItem from './UserNewsItem';
import type { InspectAssessmentNewsDisplayRow } from './UserNewsItem';
import { formatAssessmentDate } from './formatters';
import { describeIcs, ICS_HELP_TEXT } from './ics';
import type {
  InspectAssessmentPickerGroup,
  InspectAssessmentPickerOption,
} from './pickerOptions';
import {
  assessmentHasData,
  buildAssessmentPickerGroups,
  buildAssessmentPickerOptions,
  buildKlassPickerOptions,
  describePickerFilter,
  klassHasData,
  userHasData,
} from './pickerOptions';
import {
  buildUserAssignmentIndex,
  describeUserSelection,
} from './userAssignments';
import './styles.css';

const MAX_BACKGROUND_BATCH_PAIRS = 32;
const BACKGROUND_BATCH_DELAY_MS = 500;
const EMPTY_USER_ASSIGNMENTS: InspectAssessmentUserAssignment[] = [];
const EMPTY_USER_ATTEMPTS: InspectAssessmentUserAttempt[] = [];
const EMPTY_PILLAR_ONE_PERIODS: InspectAssessmentPillarOnePeriod[] = [];
const EMPTY_INSTRUMENT_CODES: string[] = [];

const PICKER_INTRO =
  'Pick an assessment to read the exercise and its answer key. Add a klass and a candidate on top to see how that person was graded against it.';

type AssessmentWorkspaceView = 'per-period' | 'full';
type AssessmentDetailTab =
  | 'glance-assessment'
  | 'pillar-1-score'
  | 'pillar-2-score'
  | 'pillar-3-score';

const NewsList: React.FC<{
  emptyText: string;
  instrumentCode?: string;
  rows: InspectAssessmentNewsDisplayRow[];
  selectionStyle?: 'highlight' | 'incorrect';
  sourcePeriodIndex: number;
}> = ({
  emptyText,
  instrumentCode,
  rows,
  selectionStyle,
  sourcePeriodIndex,
}) =>
  rows.length === 0 ? (
    <Typography.Text type="secondary">{emptyText}</Typography.Text>
  ) : (
    <div className="assessment-inspect-assessment__news-list">
      {rows.map(row => (
        <UserNewsItem
          instrumentCode={instrumentCode}
          key={row.news.id}
          row={row}
          selectionStyle={selectionStyle}
          sourcePeriodIndex={sourcePeriodIndex}
        />
      ))}
    </div>
  );

/**
 * One dropdown row: the name on top, how much data it holds underneath. The
 * closed select keeps the plain label, so it still reads as one line there.
 */
const renderPickerOption = (option: InspectAssessmentPickerOption) => (
  <div className="assessment-inspect-assessment__option" title={option.tooltip}>
    <span className="assessment-inspect-assessment__option-label">
      {option.label}
    </span>
    {option.detail && (
      <span className="assessment-inspect-assessment__option-detail">
        {option.detail}
      </span>
    )}
  </div>
);

/**
 * The assessment select holds either grouped rows or one flat list, so its
 * option type is the union of the two. antd draws a group's own heading and
 * only calls optionRender for the rows inside it; the narrowing keeps the
 * types honest about that.
 */
type AssessmentSelectOption =
  | InspectAssessmentPickerOption
  | InspectAssessmentPickerGroup;

const renderAssessmentOption = (option: AssessmentSelectOption) =>
  'options' in option ? option.label : renderPickerOption(option);

const pickerNotFoundContent = (fetching: boolean, emptyText: string) =>
  fetching ? (
    <div className="assessment-inspect-assessment__centered">
      <Spin size="small" />
    </div>
  ) : (
    <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
  );

const InspectAssessment: React.FC = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectInspectAssessmentState);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [workspaceView, setWorkspaceView] =
    React.useState<AssessmentWorkspaceView>('per-period');
  const [detailTab, setDetailTab] =
    React.useState<AssessmentDetailTab>('glance-assessment');
  // Off by default: the lists hold only what can actually be inspected.
  const [showEmptyOptions, setShowEmptyOptions] = React.useState(false);
  const includeEmpty = showEmptyOptions ? (1 as const) : undefined;
  // How long each list is with nothing filtered out, once it has been seen.
  // It is what lets the filtered list say how many rows it is holding back.
  const [knownTotals, setKnownTotals] = React.useState<Record<string, number>>(
    {},
  );
  const rememberTotal = React.useCallback((key: string, total: number) => {
    setKnownTotals(current =>
      current[key] === total ? current : { ...current, [key]: total },
    );
  }, []);
  const [pageVisible, setPageVisible] = React.useState(
    () =>
      typeof document === 'undefined' || document.visibilityState === 'visible',
  );
  const userAssignmentsContextKey =
    state.selectedAssessmentId !== undefined &&
    state.selectedKlassId !== undefined &&
    state.selectedUserId !== undefined
      ? inspectAssessmentUserAssignmentsContextKey(
          state.selectedAssessmentId,
          state.selectedKlassId,
          state.selectedUserId,
        )
      : undefined;
  const userAssignmentsCacheEntry = userAssignmentsContextKey
    ? state.userAssignmentsByContextKey[userAssignmentsContextKey]
    : undefined;
  const selectedUserAssignmentSessionId =
    userAssignmentsCacheEntry?.selectedSessionId;
  const userAssignmentsRequestKey =
    state.selectedAssessmentId !== undefined &&
    state.selectedKlassId !== undefined &&
    state.selectedUserId !== undefined
      ? inspectAssessmentUserAssignmentsRequestKey({
          assessment_id: state.selectedAssessmentId,
          klass_id: state.selectedKlassId,
          user_id: state.selectedUserId,
          session_id: selectedUserAssignmentSessionId,
        })
      : undefined;
  const userAssignmentAttempts =
    userAssignmentsCacheEntry?.attempts ?? EMPTY_USER_ATTEMPTS;
  const userAssignments =
    selectedUserAssignmentSessionId === undefined
      ? EMPTY_USER_ASSIGNMENTS
      : (userAssignmentsCacheEntry?.assignmentsBySessionId[
          selectedUserAssignmentSessionId
        ] ?? EMPTY_USER_ASSIGNMENTS);
  const userAssignmentsFetching = Boolean(
    userAssignmentsRequestKey &&
      userAssignmentsCacheEntry?.fetchingByRequestKey[
        userAssignmentsRequestKey
      ],
  );
  const userAssignmentsFetched = Boolean(
    userAssignmentsRequestKey &&
      userAssignmentsCacheEntry?.fetchedByRequestKey[userAssignmentsRequestKey],
  );
  const userAssignmentsError = userAssignmentsRequestKey
    ? userAssignmentsCacheEntry?.errorsByRequestKey[userAssignmentsRequestKey]
    : undefined;
  const orphanedUserAssignmentCount =
    selectedUserAssignmentSessionId === undefined
      ? 0
      : (userAssignmentsCacheEntry?.orphanedAssignmentCountBySessionId[
          selectedUserAssignmentSessionId
        ] ?? 0);
  const pillarOnePeriods =
    selectedUserAssignmentSessionId === undefined
      ? EMPTY_PILLAR_ONE_PERIODS
      : (userAssignmentsCacheEntry?.pillarOnePeriodsBySessionId[
          selectedUserAssignmentSessionId
        ] ?? EMPTY_PILLAR_ONE_PERIODS);
  const pillarOneInstrumentCodes =
    selectedUserAssignmentSessionId === undefined
      ? EMPTY_INSTRUMENT_CODES
      : (userAssignmentsCacheEntry?.pillarOneInstrumentCodesBySessionId[
          selectedUserAssignmentSessionId
        ] ?? EMPTY_INSTRUMENT_CODES);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const updateVisibility = () =>
      setPageVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', updateVisibility);
    return () =>
      document.removeEventListener('visibilitychange', updateVisibility);
  }, []);

  // Each list is fetched for one scope. The store remembers which scope it
  // asked for, so a failed fetch is not retried on every render, while a
  // changed scope — another assessment, or "Show all" — asks again.
  const assessmentsRequest = React.useMemo<InspectAssessmentsRequest>(
    () => ({ include_empty: includeEmpty }),
    [includeEmpty],
  );
  const wantedAssessmentsKey = inspectAssessmentsRequestKey(assessmentsRequest);
  useEffect(() => {
    if (state.assessmentsRequestKey === wantedAssessmentsKey) return;
    dispatch(fetchInspectAssessments(assessmentsRequest));
  }, [
    assessmentsRequest,
    dispatch,
    state.assessmentsRequestKey,
    wantedAssessmentsKey,
  ]);

  const klassesRequest = React.useMemo<InspectAssessmentKlassesRequest>(
    () => ({
      assessment_id: state.selectedAssessmentId,
      include_empty: includeEmpty,
    }),
    [includeEmpty, state.selectedAssessmentId],
  );
  const wantedKlassesKey = inspectAssessmentKlassesRequestKey(klassesRequest);
  useEffect(() => {
    if (state.klassesRequestKey === wantedKlassesKey) return;
    dispatch(fetchInspectAssessmentKlasses(klassesRequest));
  }, [dispatch, klassesRequest, state.klassesRequestKey, wantedKlassesKey]);

  const usersRequest = React.useMemo<InspectAssessmentUsersRequest | undefined>(
    () =>
      state.selectedKlassId === undefined
        ? undefined
        : {
            klass_id: state.selectedKlassId,
            assessment_id: state.selectedAssessmentId,
            include_empty: includeEmpty,
          },
    [includeEmpty, state.selectedAssessmentId, state.selectedKlassId],
  );
  const wantedUsersKey = usersRequest
    ? inspectAssessmentUsersRequestKey(usersRequest)
    : undefined;
  useEffect(() => {
    if (!usersRequest || state.usersRequestKey === wantedUsersKey) return;
    dispatch(fetchInspectAssessmentUsers(usersRequest));
  }, [dispatch, state.usersRequestKey, usersRequest, wantedUsersKey]);

  const klassesSettled =
    state.klassesRequestKey === wantedKlassesKey &&
    !state.klassesFetching &&
    !state.klassesError;
  const usersSettled =
    usersRequest !== undefined &&
    state.usersRequestKey === wantedUsersKey &&
    !state.usersFetching &&
    !state.usersError;

  // A klass that the new scope no longer lists cannot stay selected, or the
  // klass select would show a value that is not in its own list.
  useEffect(() => {
    if (state.selectedKlassId === undefined || !klassesSettled) return;
    if (state.klasses.some(klass => klass.id === state.selectedKlassId)) return;
    dispatch(selectInspectAssessmentKlass(undefined));
  }, [dispatch, klassesSettled, state.klasses, state.selectedKlassId]);

  const assessmentsSettled =
    state.assessmentsRequestKey === wantedAssessmentsKey &&
    !state.assessmentsFetching &&
    !state.assessmentsError;

  // The klass axis has always let go of a selection its list no longer
  // holds; the assessment axis needs the same, or switching Show all off
  // with a never-played key selected leaves the select showing a value that
  // is not in its own list.
  useEffect(() => {
    if (state.selectedAssessmentId === undefined || !assessmentsSettled) return;
    if (
      state.assessments.some(
        assessment => assessment.id === state.selectedAssessmentId,
      )
    )
      return;
    dispatch(selectInspectAssessment(undefined));
  }, [
    assessmentsSettled,
    dispatch,
    state.assessments,
    state.selectedAssessmentId,
  ]);

  const klassesTotalKey = `klasses:${state.selectedAssessmentId ?? 'any'}`;
  const usersTotalKey = `users:${state.selectedKlassId ?? 'none'}:${
    state.selectedAssessmentId ?? 'any'
  }`;

  useEffect(() => {
    if (!showEmptyOptions || !assessmentsSettled) return;
    rememberTotal('assessments', state.assessments.length);
  }, [
    assessmentsSettled,
    rememberTotal,
    showEmptyOptions,
    state.assessments.length,
  ]);
  useEffect(() => {
    if (!showEmptyOptions || !klassesSettled) return;
    rememberTotal(klassesTotalKey, state.klasses.length);
  }, [
    klassesSettled,
    klassesTotalKey,
    rememberTotal,
    showEmptyOptions,
    state.klasses.length,
  ]);
  useEffect(() => {
    if (!showEmptyOptions || !usersSettled) return;
    rememberTotal(usersTotalKey, state.users.length);
  }, [
    rememberTotal,
    showEmptyOptions,
    state.users.length,
    usersSettled,
    usersTotalKey,
  ]);

  const assessmentsLoading =
    state.assessmentsFetching ||
    state.assessmentsRequestKey !== wantedAssessmentsKey;
  const klassesLoading =
    state.klassesFetching || state.klassesRequestKey !== wantedKlassesKey;
  const usersLoading =
    state.selectedKlassId !== undefined &&
    !state.usersError &&
    (state.usersFetching || state.usersRequestKey !== wantedUsersKey);
  const instrumentsLoading =
    state.selectedAssessmentId !== undefined &&
    !state.instrumentsError &&
    (state.instrumentsFetching ||
      state.instrumentsAssessmentId !== state.selectedAssessmentId);
  const periodsLoading =
    state.selectedAssessmentId !== undefined &&
    !state.periodsError &&
    (state.periodsFetching ||
      state.periodsAssessmentId !== state.selectedAssessmentId);

  // Which lists the server actually narrowed. The assessments and klasses
  // endpoints filter whenever include_empty is absent; the users endpoint
  // only filters when an assessment is sent with it, so with no assessment
  // selected the whole roster comes back however the switch is set.
  const assessmentsServerFiltered = !showEmptyOptions;
  const klassesServerFiltered = !showEmptyOptions;
  const usersServerFiltered =
    !showEmptyOptions && state.selectedAssessmentId !== undefined;

  const assessmentsSummary = describePickerFilter({
    noun: 'assessment',
    nounPlural: 'assessments',
    shown: state.assessments.length,
    withData: state.assessments.filter(assessmentHasData).length,
    serverFiltered: assessmentsServerFiltered,
    loading: assessmentsLoading,
    failed: Boolean(state.assessmentsError),
    knownTotal: knownTotals.assessments,
  });
  const klassesSummary = describePickerFilter({
    noun: 'klass',
    nounPlural: 'klasses',
    shown: state.klasses.length,
    withData: state.klasses.filter(klassHasData).length,
    serverFiltered: klassesServerFiltered,
    loading: klassesLoading,
    failed: Boolean(state.klassesError),
    knownTotal: knownTotals[klassesTotalKey],
  });
  const usersSummary =
    state.selectedKlassId === undefined
      ? ''
      : describePickerFilter({
          noun: 'candidate',
          nounPlural: 'candidates',
          shown: state.users.length,
          withData: state.users.filter(userHasData).length,
          serverFiltered: usersServerFiltered,
          loading: usersLoading,
          failed: Boolean(state.usersError),
          knownTotal: knownTotals[usersTotalKey],
        });

  useEffect(() => {
    const assessmentId = state.selectedAssessmentId;
    const klassId = state.selectedKlassId;
    const userId = state.selectedUserId;
    if (
      assessmentId === undefined ||
      klassId === undefined ||
      userId === undefined ||
      userAssignmentsContextKey === undefined ||
      userAssignmentsFetching ||
      userAssignmentsFetched
    )
      return;
    dispatch(
      fetchInspectAssessmentUserAssignments({
        assessment_id: assessmentId,
        klass_id: klassId,
        user_id: userId,
        session_id: selectedUserAssignmentSessionId,
      }),
    );
  }, [
    dispatch,
    state.selectedAssessmentId,
    state.selectedKlassId,
    state.selectedUserId,
    selectedUserAssignmentSessionId,
    userAssignmentsContextKey,
    userAssignmentsFetched,
    userAssignmentsFetching,
  ]);

  const options = React.useMemo(
    () => buildAssessmentPickerOptions(state.assessments),
    [state.assessments],
  );
  // Grouped when the server sends session counts, one flat list when it does
  // not: an older server cannot say which key was ever played.
  const assessmentGroups = React.useMemo(
    () => buildAssessmentPickerGroups(state.assessments),
    [state.assessments],
  );
  const assessmentSelectOptions: AssessmentSelectOption[] =
    assessmentGroups ?? options;
  const klassOptions = React.useMemo(
    () => buildKlassPickerOptions(state.klasses),
    [state.klasses],
  );
  const userAttemptOptions = userAssignmentAttempts.map(attempt => ({
    value: attempt.session_id,
    label: `Session #${attempt.session_id} · ${formatAssessmentDate(
      attempt.created_at,
    )}${
      attempt.finish_date
        ? ` -> ${formatAssessmentDate(attempt.finish_date)}`
        : ' · unfinished'
    }`,
  }));
  const selectedUserAttempt = userAssignmentAttempts.find(
    attempt => attempt.session_id === selectedUserAssignmentSessionId,
  );
  const sliderDirectionRequest = React.useMemo<
    InspectAssessmentSliderDirectionRequest | undefined
  >(
    () =>
      state.selectedAssessmentId === undefined || !selectedUserAttempt
        ? undefined
        : {
            assessment_id: state.selectedAssessmentId,
            session_id: selectedUserAttempt.session_id,
            link_id: selectedUserAttempt.link_id,
            player_id: selectedUserAttempt.player_id,
          },
    [state.selectedAssessmentId, selectedUserAttempt],
  );
  const sliderDirectionRequestKey = sliderDirectionRequest
    ? inspectAssessmentSliderDirectionRequestKey(sliderDirectionRequest)
    : undefined;
  const sliderDirectionAudit = sliderDirectionRequestKey
    ? state.sliderDirectionByRequestKey[sliderDirectionRequestKey]
    : undefined;
  const sliderDirectionFetching = Boolean(
    sliderDirectionRequestKey &&
      state.sliderDirectionFetchingByRequestKey[sliderDirectionRequestKey],
  );
  const sliderDirectionFetched = Boolean(
    sliderDirectionRequestKey &&
      state.sliderDirectionFetchedByRequestKey[sliderDirectionRequestKey],
  );
  const sliderDirectionError = sliderDirectionRequestKey
    ? state.sliderDirectionErrorByRequestKey[sliderDirectionRequestKey]
    : undefined;
  const loadSliderDirection = React.useCallback(() => {
    if (!sliderDirectionRequest) return;
    dispatch(fetchInspectAssessmentSliderDirection(sliderDirectionRequest));
  }, [dispatch, sliderDirectionRequest]);
  const candidatePositionsRequest = sliderDirectionRequest;
  const candidatePositionsRequestKey = candidatePositionsRequest
    ? inspectAssessmentCandidatePositionsRequestKey(candidatePositionsRequest)
    : undefined;
  const candidatePositionEvidence = candidatePositionsRequestKey
    ? state.candidatePositionsByRequestKey[candidatePositionsRequestKey]
    : undefined;
  const candidatePositionsFetching = Boolean(
    candidatePositionsRequestKey &&
      state.candidatePositionsFetchingByRequestKey[
        candidatePositionsRequestKey
      ],
  );
  const candidatePositionsFetched = Boolean(
    candidatePositionsRequestKey &&
      state.candidatePositionsFetchedByRequestKey[candidatePositionsRequestKey],
  );
  const candidatePositionsError = candidatePositionsRequestKey
    ? state.candidatePositionsErrorByRequestKey[candidatePositionsRequestKey]
    : undefined;
  const loadCandidatePositions = React.useCallback(() => {
    if (!candidatePositionsRequest) return;
    dispatch(
      ensureInspectAssessmentCandidatePositions(candidatePositionsRequest),
    );
  }, [candidatePositionsRequest, dispatch]);
  const optimalPortfolioRequest = React.useMemo<
    InspectAssessmentOptimalPortfolioRequest | undefined
  >(
    () =>
      state.selectedAssessmentId === undefined
        ? undefined
        : {
            assessment_id: state.selectedAssessmentId,
            ...(selectedUserAttempt
              ? { link_id: selectedUserAttempt.link_id }
              : {}),
          },
    [state.selectedAssessmentId, selectedUserAttempt],
  );
  const optimalPortfolioRequestKey = optimalPortfolioRequest
    ? inspectAssessmentOptimalPortfolioRequestKey(optimalPortfolioRequest)
    : undefined;
  const optimalPortfolio = optimalPortfolioRequestKey
    ? state.optimalPortfolioByRequestKey[optimalPortfolioRequestKey]
    : undefined;
  const optimalPortfolioFetching = Boolean(
    optimalPortfolioRequestKey &&
      state.optimalPortfolioFetchingByRequestKey[optimalPortfolioRequestKey],
  );
  const optimalPortfolioFetched = Boolean(
    optimalPortfolioRequestKey &&
      state.optimalPortfolioFetchedByRequestKey[optimalPortfolioRequestKey],
  );
  const optimalPortfolioError = optimalPortfolioRequestKey
    ? state.optimalPortfolioErrorByRequestKey[optimalPortfolioRequestKey]
    : undefined;
  const loadOptimalPortfolio = React.useCallback(() => {
    if (!optimalPortfolioRequest) return;
    dispatch(fetchInspectAssessmentOptimalPortfolio(optimalPortfolioRequest));
  }, [dispatch, optimalPortfolioRequest]);
  const blueprintLinkText = state.periodsBlueprint
    ? `linked to Blueprint ${state.periodsBlueprint.blueprint_name} ID#${state.periodsBlueprint.blueprint_id}, latest published template_session ID#${state.periodsBlueprint.template_session_id}`
    : '';
  const selectedPeriod = state.periods.find(
    period => period.index === state.selectedPeriodIndex,
  );
  const selectedAssessmentPeriod = selectedPeriod?.assessment_period;
  const selectedInstrument = state.instruments.find(
    instrument => instrument.id === state.selectedInstrumentId,
  );
  const assessmentPeriodStartMismatch = Boolean(
    selectedPeriod &&
      selectedAssessmentPeriod &&
      selectedPeriod.scenario_date !== selectedAssessmentPeriod.start_date,
  );
  const selectedPeriodNews =
    state.selectedPeriodIndex === undefined
      ? undefined
      : state.periodNewsByIndex[state.selectedPeriodIndex];
  const selectedPeriodNewsFetching = Boolean(
    !selectedPeriodNews &&
      (state.periodNewsFetching ||
        (state.selectedPeriodIndex !== undefined &&
          state.periodNewsBatchFetchingByIndex[state.selectedPeriodIndex])),
  );
  const selectedPeriodNewsError =
    state.periodNewsAssessmentId === state.selectedAssessmentId &&
    state.periodNewsPeriodIndex === state.selectedPeriodIndex
      ? state.periodNewsError
      : undefined;
  const selectedPeriodPriceKey =
    state.selectedAssessmentId !== undefined &&
    state.selectedPeriodIndex !== undefined &&
    state.selectedInstrumentId !== undefined
      ? inspectAssessmentPeriodPriceKey(
          state.selectedAssessmentId,
          state.selectedPeriodIndex,
          state.selectedInstrumentId,
        )
      : undefined;
  const selectedPeriodPrices = selectedPeriodPriceKey
    ? state.periodPricesByKey[selectedPeriodPriceKey]
    : undefined;
  const anyPeriodPricesForegroundFetching = Object.values(
    state.periodPricesFetchingByKey,
  ).some(Boolean);
  const selectedPeriodPricesFetching = Boolean(
    !selectedPeriodPrices &&
      (anyPeriodPricesForegroundFetching ||
        (selectedPeriodPriceKey &&
          state.periodPricesBatchFetchingByKey[selectedPeriodPriceKey])),
  );
  const selectedPeriodPricesError = selectedPeriodPriceKey
    ? state.periodPricesErrorByKey[selectedPeriodPriceKey]
    : undefined;
  // The background batch prefetches the other periods. When it fails it stops
  // for good, so the per-period view has to say why nothing else arrives.
  const periodBatchError =
    state.periodBatchAssessmentId === state.selectedAssessmentId
      ? state.periodBatchError
      : undefined;
  const userAssignmentIndex = React.useMemo(
    () => buildUserAssignmentIndex(userAssignments, state.periods),
    [state.periods, userAssignments],
  );
  const newsRows = React.useMemo(
    () =>
      (selectedPeriodNews?.items ?? []).map(news => ({
        news,
        ics: describeIcs(news, selectedInstrument),
        userSelection: describeUserSelection(
          userAssignmentIndex,
          news.id,
          selectedInstrument?.id,
          state.selectedPeriodIndex ?? 0,
        ),
      })),
    [
      selectedInstrument,
      selectedPeriodNews,
      state.selectedPeriodIndex,
      userAssignmentIndex,
    ],
  );
  const nonNeutralNewsRows = React.useMemo(
    () =>
      newsRows
        .filter(
          row => row.ics.tone === 'positive' || row.ics.tone === 'negative',
        )
        .sort((left, right) => (right.ics.value ?? 0) - (left.ics.value ?? 0)),
    [newsRows],
  );
  const userSelectedNonNeutralNewsCount = React.useMemo(
    () => nonNeutralNewsRows.filter(row => row.userSelection.selected).length,
    [nonNeutralNewsRows],
  );
  const otherUserSelectedNewsRows = React.useMemo(
    () =>
      newsRows.filter(
        row =>
          row.userSelection.selected &&
          row.ics.tone !== 'positive' &&
          row.ics.tone !== 'negative',
      ),
    [newsRows],
  );
  const nonNeutralIcsSum = React.useMemo(
    () =>
      nonNeutralNewsRows.reduce((sum, row) => sum + (row.ics.value ?? 0), 0),
    [nonNeutralNewsRows],
  );
  const formattedNonNeutralIcsSum = `${nonNeutralIcsSum > 0 ? '+' : ''}${nonNeutralIcsSum.toFixed(2)}`;

  const backgroundBatch = React.useMemo<
    InspectAssessmentPeriodsBatchRequest | undefined
  >(() => {
    const assessmentId = state.selectedAssessmentId;
    const selectedPeriodIndex = state.selectedPeriodIndex;
    const selectedInstrumentId = state.selectedInstrumentId;
    if (
      !pageVisible ||
      assessmentId === undefined ||
      selectedPeriodIndex === undefined ||
      selectedInstrumentId === undefined ||
      !selectedPeriodPrices ||
      !selectedPeriodNews ||
      state.periodsFetching ||
      state.instrumentsFetching ||
      state.periodBatchFetching ||
      state.periodBatchError ||
      state.periodNewsFetching ||
      anyPeriodPricesForegroundFetching
    )
      return undefined;

    const orderedPeriodIndices = state.periods
      .map(period => period.index)
      .sort(
        (left, right) =>
          Math.abs(left - selectedPeriodIndex) -
            Math.abs(right - selectedPeriodIndex) || left - right,
      );
    const pairNeedsPrices = (periodIndex: number, instrumentId: number) => {
      const key = inspectAssessmentPeriodPriceKey(
        assessmentId,
        periodIndex,
        instrumentId,
      );
      return (
        !state.periodPricesByKey[key] &&
        !state.periodPricesFetchingByKey[key] &&
        !state.periodPricesBatchFetchingByKey[key] &&
        !state.periodPricesErrorByKey[key]
      );
    };
    const periodNeedsNews = (periodIndex: number) =>
      !state.periodNewsByIndex[periodIndex] &&
      !state.periodNewsBatchFetchingByIndex[periodIndex];
    const makeBatch = (
      periodIndices: number[],
      instrumentIds: number[],
    ): InspectAssessmentPeriodsBatchRequest => ({
      assessment_id: assessmentId,
      period_indices: periodIndices.join(','),
      instrument_ids:
        instrumentIds.length > 0 ? instrumentIds.join(',') : undefined,
      include_news: periodIndices.some(periodNeedsNews) ? 'true' : 'false',
    });

    const selectedInstrumentPeriods = orderedPeriodIndices
      .filter(periodIndex => pairNeedsPrices(periodIndex, selectedInstrumentId))
      .slice(0, MAX_BACKGROUND_BATCH_PAIRS);
    if (selectedInstrumentPeriods.length > 0) {
      return makeBatch(selectedInstrumentPeriods, [selectedInstrumentId]);
    }

    const selectedPeriodInstruments = state.instruments
      .map(instrument => instrument.id)
      .filter(instrumentId =>
        pairNeedsPrices(selectedPeriodIndex, instrumentId),
      )
      .slice(0, MAX_BACKGROUND_BATCH_PAIRS);
    if (selectedPeriodInstruments.length > 0) {
      return makeBatch([selectedPeriodIndex], selectedPeriodInstruments);
    }

    const remainingInstruments = state.instruments
      .map(instrument => instrument.id)
      .sort(
        (left, right) =>
          Number(right === selectedInstrumentId) -
          Number(left === selectedInstrumentId),
      );
    for (const instrumentId of remainingInstruments) {
      const periodIndices = orderedPeriodIndices
        .filter(periodIndex => pairNeedsPrices(periodIndex, instrumentId))
        .slice(0, MAX_BACKGROUND_BATCH_PAIRS);
      if (periodIndices.length > 0) {
        return makeBatch(periodIndices, [instrumentId]);
      }
    }

    const newsOnlyPeriodIndices = orderedPeriodIndices
      .filter(periodNeedsNews)
      .slice(0, MAX_BACKGROUND_BATCH_PAIRS);
    if (newsOnlyPeriodIndices.length > 0) {
      return makeBatch(newsOnlyPeriodIndices, []);
    }
    return undefined;
  }, [
    pageVisible,
    anyPeriodPricesForegroundFetching,
    selectedPeriodNews,
    selectedPeriodPrices,
    state.instruments,
    state.instrumentsFetching,
    state.periodBatchError,
    state.periodBatchFetching,
    state.periodNewsBatchFetchingByIndex,
    state.periodNewsByIndex,
    state.periodNewsFetching,
    state.periodPricesByKey,
    state.periodPricesBatchFetchingByKey,
    state.periodPricesErrorByKey,
    state.periodPricesFetchingByKey,
    state.periods,
    state.periodsFetching,
    state.selectedAssessmentId,
    state.selectedInstrumentId,
    state.selectedPeriodIndex,
  ]);

  useEffect(() => {
    if (!backgroundBatch) return undefined;
    const timer = window.setTimeout(
      () => dispatch(fetchInspectAssessmentPeriodBatch(backgroundBatch)),
      BACKGROUND_BATCH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [backgroundBatch, dispatch]);

  useEffect(() => {
    if (
      !selectedPeriodPriceKey ||
      state.selectedAssessmentId === undefined ||
      state.selectedPeriodIndex === undefined ||
      state.selectedInstrumentId === undefined ||
      selectedPeriodPrices ||
      anyPeriodPricesForegroundFetching ||
      selectedPeriodPricesError
    )
      return;

    dispatch(
      fetchInspectAssessmentPeriodPrices({
        assessment_id: state.selectedAssessmentId,
        period_index: state.selectedPeriodIndex,
        instrument_id: state.selectedInstrumentId,
        include_news: 'false',
      }),
    );
  }, [
    dispatch,
    selectedPeriodPriceKey,
    selectedPeriodPrices,
    selectedPeriodPricesError,
    anyPeriodPricesForegroundFetching,
    state.selectedAssessmentId,
    state.selectedInstrumentId,
    state.selectedPeriodIndex,
  ]);

  useEffect(() => {
    if (
      state.selectedAssessmentId === undefined ||
      state.selectedPeriodIndex === undefined ||
      selectedPeriodNews ||
      state.periodNewsFetching ||
      selectedPeriodNewsFetching ||
      selectedPeriodNewsError
    )
      return;
    dispatch(
      fetchInspectAssessmentPeriodNews({
        assessment_id: state.selectedAssessmentId,
        period_index: state.selectedPeriodIndex,
      }),
    );
  }, [
    dispatch,
    selectedPeriodNews,
    selectedPeriodNewsError,
    selectedPeriodNewsFetching,
    state.periodNewsFetching,
    state.selectedAssessmentId,
    state.selectedPeriodIndex,
  ]);

  const changeAssessment = (assessmentId?: number) => {
    dispatch(selectInspectAssessment(assessmentId));
    if (assessmentId !== undefined) {
      dispatch(
        fetchInspectAssessmentInstruments({ assessment_id: assessmentId }),
      );
      dispatch(
        fetchInspectAssessmentPeriods({
          assessment_id: assessmentId,
          period_index: 1,
        }),
      );
    }
  };

  // The users fetch follows the selection through its own effect, because the
  // list also has to be re-read when the assessment or "Show all" changes.
  const changeKlass = (klassId?: number) => {
    dispatch(selectInspectAssessmentKlass(klassId));
  };

  const changeUser = (userId?: number) => {
    dispatch(selectInspectAssessmentUser(userId));
  };

  const changeUserSession = (sessionId: number) => {
    dispatch(selectInspectAssessmentUserSession(sessionId));
  };

  const changePeriod = (periodIndex: number) => {
    dispatch(selectInspectAssessmentPeriod(periodIndex));
  };

  useEffect(() => {
    const handleArrowNavigation = (event: KeyboardEvent) => {
      if (detailTab !== 'glance-assessment') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
        return;

      const target = event.target;
      if (target instanceof Element) {
        const targetIsPageBody = target === document.body;
        if (!targetIsPageBody && !rootRef.current?.contains(target)) return;
        if (
          (target instanceof HTMLElement && target.isContentEditable) ||
          target.closest(
            'input, textarea, select, [role="combobox"], [role="listbox"], [role="menu"], [role="radio"], [role="radiogroup"], [role="slider"], [role="spinbutton"], [role="tab"], [role="tablist"], .assessment-inspect-assessment__view-selector',
          )
        )
          return;
      }

      const moveSelection = (
        ids: number[],
        selectedId: number | undefined,
        offset: -1 | 1,
        select: (id: number) => void,
      ) => {
        if (ids.length === 0) return false;
        const selectedPosition =
          selectedId === undefined ? -1 : ids.indexOf(selectedId);
        const nextPosition =
          selectedPosition === -1
            ? offset === 1
              ? 0
              : ids.length - 1
            : Math.min(ids.length - 1, Math.max(0, selectedPosition + offset));
        if (nextPosition === selectedPosition) return false;
        select(ids[nextPosition]);
        return true;
      };

      let moved = false;
      if (
        workspaceView === 'per-period' &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        moved = moveSelection(
          state.periods.map(period => period.index),
          state.selectedPeriodIndex,
          event.key === 'ArrowLeft' ? -1 : 1,
          periodIndex => dispatch(selectInspectAssessmentPeriod(periodIndex)),
        );
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        moved = moveSelection(
          state.instruments.map(instrument => instrument.id),
          state.selectedInstrumentId,
          event.key === 'ArrowUp' ? -1 : 1,
          instrumentId =>
            dispatch(selectInspectAssessmentInstrument(instrumentId)),
        );
      }
      if (moved) event.preventDefault();
    };

    window.addEventListener('keydown', handleArrowNavigation);
    return () => window.removeEventListener('keydown', handleArrowNavigation);
  }, [
    detailTab,
    dispatch,
    state.instruments,
    state.periods,
    state.selectedInstrumentId,
    state.selectedPeriodIndex,
    workspaceView,
  ]);

  return (
    <div className="assessment-inspect-assessment" ref={rootRef}>
      <div className="assessment-inspect-assessment__picker">
        <Typography.Text
          className="assessment-inspect-assessment__picker-intro"
          type="secondary"
        >
          {PICKER_INTRO}
        </Typography.Text>
        <div className="assessment-inspect-assessment__picker-row">
          {/* Both selects are disabled while their own list is in flight.
              antd keeps a loading select clickable, so the rows on screen
              are the PREVIOUS scope's — and picking one is undone a moment
              later by the auto-clear, which looks like the page ignoring
              the click. */}
          <Select<number, AssessmentSelectOption>
            allowClear
            aria-label="Assessment"
            className="assessment-inspect-assessment__select"
            disabled={assessmentsLoading}
            loading={assessmentsLoading}
            notFoundContent={pickerNotFoundContent(
              assessmentsLoading,
              showEmptyOptions
                ? 'No assessment to show'
                : 'No assessment has data yet',
            )}
            onChange={changeAssessment}
            optionFilterProp="searchText"
            optionRender={option => renderAssessmentOption(option.data)}
            options={assessmentSelectOptions}
            placeholder="Pick an assessment to inspect"
            showSearch
            status={state.assessmentsError ? 'error' : undefined}
            value={state.selectedAssessmentId}
          />
          <Select<number, InspectAssessmentPickerOption>
            allowClear
            aria-label="Klass"
            className="assessment-inspect-assessment__context-select"
            disabled={klassesLoading}
            loading={klassesLoading}
            notFoundContent={pickerNotFoundContent(
              klassesLoading,
              showEmptyOptions
                ? 'No klass to show'
                : 'No klass has played this yet',
            )}
            onChange={changeKlass}
            optionFilterProp="searchText"
            optionRender={option => renderPickerOption(option.data)}
            options={klassOptions}
            placeholder="Add a klass (optional)"
            showSearch
            status={state.klassesError ? 'error' : undefined}
            value={state.selectedKlassId}
          />
          {userAssignmentAttempts.length > 1 && (
            <Select<number>
              aria-label="Assessment session"
              className="assessment-inspect-assessment__attempt-select"
              loading={userAssignmentsFetching}
              onChange={changeUserSession}
              optionFilterProp="label"
              options={userAttemptOptions}
              placeholder="Select a session"
              showSearch
              status={userAssignmentsError ? 'error' : undefined}
              value={selectedUserAssignmentSessionId}
            />
          )}
          {state.periodsBlueprint && (
            <Tooltip placement="topLeft" title={blueprintLinkText}>
              <Typography.Text
                className="assessment-inspect-assessment__blueprint-link"
                type="secondary"
              >
                {blueprintLinkText}
              </Typography.Text>
            </Tooltip>
          )}
        </div>
        <div className="assessment-inspect-assessment__picker-row assessment-inspect-assessment__picker-summary-row">
          <Typography.Text
            className="assessment-inspect-assessment__select assessment-inspect-assessment__picker-summary"
            type="secondary"
          >
            {assessmentsSummary}
          </Typography.Text>
          <Typography.Text
            className="assessment-inspect-assessment__context-select assessment-inspect-assessment__picker-summary"
            type="secondary"
          >
            {klassesSummary}
          </Typography.Text>
          <Checkbox
            checked={showEmptyOptions}
            className="assessment-inspect-assessment__picker-toggle"
            onChange={event => setShowEmptyOptions(event.target.checked)}
          >
            Show all, including empty
          </Checkbox>
        </div>
        {state.assessmentsError && (
          <Alert
            message="Unable to load assessments"
            description={state.assessmentsError}
            type="error"
            showIcon
          />
        )}
        {state.klassesError && (
          <Alert
            message="Unable to load klasses"
            description={state.klassesError}
            type="error"
            showIcon
          />
        )}
        {/* The users error belongs to the USERS panel, which is where it is
            shown; repeating it here said the same thing twice. */}
        {userAssignmentsError && (
          <Alert
            message="Unable to load user news selections"
            description={userAssignmentsError}
            type="error"
            showIcon
          />
        )}
        {userAssignmentsContextKey !== undefined &&
          userAssignmentsFetched &&
          !userAssignmentsError &&
          userAssignmentAttempts.length === 0 && (
            <Alert
              message="No matching assessed session"
              description="This user has no session for the selected assessment and klass."
              type="info"
              showIcon
            />
          )}
        {userAssignmentAttempts.length > 1 &&
          selectedUserAssignmentSessionId === undefined && (
            <Alert
              message="Select a session attempt"
              description="This user played the selected assessment more than once in this klass."
              type="info"
              showIcon
            />
          )}
        {selectedUserAttempt?.auto_traded && (
          <Alert
            message="Auto-traded player"
            description="These news selections may have been generated by automated trade capture; individual assignment rows do not record manual versus automatic origin."
            type="warning"
            showIcon
          />
        )}
        {orphanedUserAssignmentCount > 0 && (
          <Alert
            message="Some saved selections cannot be displayed"
            description={`${orphanedUserAssignmentCount} active assignment${
              orphanedUserAssignmentCount === 1 ? '' : 's'
            } refer to news that no longer exists.`}
            type="warning"
            showIcon
          />
        )}
      </div>
      <div className="assessment-inspect-assessment__detail-layout">
        <AssessmentUserSidecar
          assessmentScoped={state.selectedAssessmentId !== undefined}
          error={state.usersError}
          fetching={usersLoading}
          onSelectUser={changeUser}
          selectedKlassId={state.selectedKlassId}
          selectedUserId={state.selectedUserId}
          showingAll={showEmptyOptions}
          summary={usersSummary}
          users={state.users}
        />
        <Tabs
          activeKey={detailTab}
          className="assessment-inspect-assessment__detail-tabs"
          destroyOnHidden={false}
          items={[
            {
              key: 'glance-assessment',
              label: 'Glance assessment',
              forceRender: true,
              children: (
                <div className="assessment-inspect-assessment__detail-panel">
                  <div className="assessment-inspect-assessment__content">
                    <InstrumentSidecar
                      instruments={state.instruments}
                      fetching={instrumentsLoading}
                      error={state.instrumentsError}
                      selectedAssessmentId={state.selectedAssessmentId}
                      selectedInstrumentId={state.selectedInstrumentId}
                      onSelectInstrument={instrumentId =>
                        dispatch(
                          selectInspectAssessmentInstrument(instrumentId),
                        )
                      }
                    />
                    <section className="assessment-inspect-assessment__workspace">
                      {workspaceView === 'per-period' && (
                        <>
                          <PeriodSidecar
                            periods={state.periods}
                            fetching={periodsLoading}
                            error={state.periodsError}
                            selectedAssessmentId={state.selectedAssessmentId}
                            selectedPeriodIndex={state.selectedPeriodIndex}
                            onSelectPeriod={changePeriod}
                          />
                          <div className="assessment-inspect-assessment__workspace-body">
                            {periodBatchError && (
                              <Alert
                                message="Loading the remaining periods stopped"
                                description={`${periodBatchError} The period you have open still loads on its own.`}
                                type="warning"
                                showIcon
                              />
                            )}
                            {selectedAssessmentPeriod && (
                              <Typography.Text>
                                Assessment period:{' '}
                                <span
                                  className={
                                    assessmentPeriodStartMismatch
                                      ? 'assessment-inspect-assessment__assessment-date--mismatch'
                                      : undefined
                                  }
                                >
                                  {formatAssessmentDate(
                                    selectedAssessmentPeriod.start_date,
                                  )}
                                </span>
                                {assessmentPeriodStartMismatch &&
                                  selectedPeriod && (
                                    <Tooltip
                                      title={`Assessment period starts on ${formatAssessmentDate(
                                        selectedAssessmentPeriod.start_date,
                                      )}, while the PM blueprint period starts on ${formatAssessmentDate(
                                        selectedPeriod.scenario_date,
                                      )}.`}
                                    >
                                      <span
                                        aria-label="Assessment and PM blueprint period dates do not match"
                                        className="assessment-inspect-assessment__date-warning"
                                        role="img"
                                        tabIndex={0}
                                      >
                                        <InfoCircleOutlined />
                                      </span>
                                    </Tooltip>
                                  )}{' '}
                                {'->'}{' '}
                                {formatAssessmentDate(
                                  selectedAssessmentPeriod.end_date,
                                )}
                              </Typography.Text>
                            )}
                            {selectedPeriod && (
                              <div className="assessment-inspect-assessment__news-columns">
                                {selectedPeriodNewsFetching ? (
                                  <div className="assessment-inspect-assessment__news-status">
                                    <Spin size="small" />
                                  </div>
                                ) : selectedPeriodNewsError ? (
                                  <div className="assessment-inspect-assessment__news-status">
                                    <Alert
                                      message="Unable to load period news"
                                      description={selectedPeriodNewsError}
                                      type="error"
                                      showIcon
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <section className="assessment-inspect-assessment__news">
                                      <div className="assessment-inspect-assessment__news-heading">
                                        <Typography.Text strong>
                                          All news of {selectedPeriod.label}
                                        </Typography.Text>
                                        {selectedInstrument && (
                                          <Typography.Text type="secondary">
                                            {selectedInstrument.code} ·{' '}
                                            {selectedInstrument.ics_source ===
                                            'parent'
                                              ? `Impact (ICS) via ${selectedInstrument.ics_code}`
                                              : selectedInstrument.ics_source ===
                                                  'direct'
                                                ? 'Impact (ICS)'
                                                : 'No assessed ICS'}
                                            <Tooltip title={ICS_HELP_TEXT}>
                                              <span
                                                aria-label="What ICS means"
                                                className="assessment-inspect-assessment__ics-help"
                                                role="img"
                                                tabIndex={0}
                                              >
                                                <QuestionCircleOutlined />
                                              </span>
                                            </Tooltip>
                                          </Typography.Text>
                                        )}
                                      </div>
                                      <NewsList
                                        emptyText="No news was shown in this period"
                                        instrumentCode={
                                          selectedInstrument?.code
                                        }
                                        rows={newsRows}
                                        sourcePeriodIndex={selectedPeriod.index}
                                      />
                                    </section>
                                    <section className="assessment-inspect-assessment__news">
                                      <div className="assessment-inspect-assessment__news-heading">
                                        <Typography.Text strong>
                                          Non-neutral news (
                                          {nonNeutralNewsRows.length})
                                          {selectedUserAttempt
                                            ? ` · User selected (${userSelectedNonNeutralNewsCount})`
                                            : ''}
                                          {' · '}
                                          {selectedPeriod.label}
                                          {selectedInstrument
                                            ? ` for ${selectedInstrument.code}`
                                            : ''}
                                        </Typography.Text>
                                        {selectedInstrument && (
                                          <Tag
                                            bordered={false}
                                            className="assessment-inspect-assessment__ics-sum"
                                            color={
                                              nonNeutralIcsSum > 0
                                                ? 'green'
                                                : nonNeutralIcsSum < 0
                                                  ? 'red'
                                                  : undefined
                                            }
                                          >
                                            ICS sum {formattedNonNeutralIcsSum}
                                          </Tag>
                                        )}
                                      </div>
                                      <NewsList
                                        emptyText={
                                          selectedInstrument
                                            ? `No non-neutral ICS values for ${selectedInstrument.code} in ${selectedPeriod.label}`
                                            : 'Select an instrument to see non-neutral ICS values'
                                        }
                                        instrumentCode={
                                          selectedInstrument?.code
                                        }
                                        rows={nonNeutralNewsRows}
                                        selectionStyle="highlight"
                                        sourcePeriodIndex={selectedPeriod.index}
                                      />
                                      {selectedUserAttempt && (
                                        <>
                                          <div className="assessment-inspect-assessment__news-heading assessment-inspect-assessment__other-news-heading">
                                            <Typography.Text strong>
                                              Other user-selected news
                                            </Typography.Text>
                                          </div>
                                          <NewsList
                                            emptyText={
                                              userAssignmentsFetching
                                                ? 'Loading user selections…'
                                                : `No other news selected for ${
                                                    selectedInstrument?.code ??
                                                    'this instrument'
                                                  }`
                                            }
                                            instrumentCode={
                                              selectedInstrument?.code
                                            }
                                            rows={otherUserSelectedNewsRows}
                                            selectionStyle="incorrect"
                                            sourcePeriodIndex={
                                              selectedPeriod.index
                                            }
                                          />
                                        </>
                                      )}
                                    </section>
                                  </>
                                )}
                                <InstrumentPriceChart
                                  data={selectedPeriodPrices}
                                  error={selectedPeriodPricesError}
                                  instrumentCode={selectedInstrument?.code}
                                  loading={selectedPeriodPricesFetching}
                                  periodLabel={selectedPeriod.label}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {workspaceView === 'full' && (
                        <FullAssessmentWorkspace
                          hasSelectedUserAttempt={Boolean(selectedUserAttempt)}
                          state={state}
                          userAssignments={userAssignments}
                          userAssignmentsFetching={userAssignmentsFetching}
                        />
                      )}
                    </section>
                  </div>
                </div>
              ),
            },
            {
              key: 'pillar-1-score',
              label: 'Pillar 1 scoring (News, direction, conviction)',
              children: (
                <PillarOneComponentTabs
                  newsMapping={newsMappingActive => (
                    <div
                      aria-label="Pillar 1 score"
                      className="assessment-inspect-assessment__detail-panel assessment-inspect-assessment__detail-panel--pillar-one"
                    >
                      <section
                        aria-label="Pillar 1 score workspace"
                        className="assessment-inspect-assessment__pillar-one-workspace"
                      >
                        <PillarOneScoreWorkspace
                          active={
                            detailTab === 'pillar-1-score' && newsMappingActive
                          }
                          assessmentId={state.selectedAssessmentId}
                          assignments={userAssignments}
                          error={
                            state.periodBatchError ?? state.periodNewsError
                          }
                          fetching={userAssignmentsFetching}
                          hasSelectedAttempt={Boolean(selectedUserAttempt)}
                          instruments={state.instruments}
                          periodNewsByIndex={state.periodNewsByIndex}
                          periodPricesByKey={state.periodPricesByKey}
                          periods={state.periods}
                          pillarOneInstrumentCodes={pillarOneInstrumentCodes}
                          pillarOnePeriods={pillarOnePeriods}
                        />
                      </section>
                      <PillarOneScoreGuide />
                    </div>
                  )}
                  sliderDirection={sliderDirectionActive => (
                    <PillarOneSliderDirectionWorkspace
                      active={
                        detailTab === 'pillar-1-score' && sliderDirectionActive
                      }
                      assessmentId={state.selectedAssessmentId}
                      audit={sliderDirectionAudit}
                      error={sliderDirectionError}
                      fetched={sliderDirectionFetched}
                      fetching={sliderDirectionFetching}
                      hasSelectedAttempt={Boolean(selectedUserAttempt)}
                      onLoad={loadSliderDirection}
                      periodPricesByKey={state.periodPricesByKey}
                      periods={state.periods}
                    />
                  )}
                  sliderMagnitude={sliderMagnitudeActive => (
                    <PillarOneSliderMagnitudeWorkspace
                      active={
                        detailTab === 'pillar-1-score' && sliderMagnitudeActive
                      }
                      assessmentId={state.selectedAssessmentId}
                      audit={sliderDirectionAudit}
                      error={sliderDirectionError}
                      fetched={sliderDirectionFetched}
                      fetching={sliderDirectionFetching}
                      hasSelectedAttempt={Boolean(selectedUserAttempt)}
                      onLoad={loadSliderDirection}
                      periodPricesByKey={state.periodPricesByKey}
                      periods={state.periods}
                    />
                  )}
                  restraint={restraintActive => (
                    <PillarOneRestraintWorkspace
                      active={detailTab === 'pillar-1-score' && restraintActive}
                      assessmentId={state.selectedAssessmentId}
                      audit={sliderDirectionAudit}
                      error={sliderDirectionError}
                      fetched={sliderDirectionFetched}
                      fetching={sliderDirectionFetching}
                      hasSelectedAttempt={Boolean(selectedUserAttempt)}
                      onLoad={loadSliderDirection}
                      periodPricesByKey={state.periodPricesByKey}
                      periods={state.periods}
                    />
                  )}
                />
              ),
            },
            {
              key: 'pillar-2-score',
              label: 'Pillar 2 scoring (optimal portfolio)',
              destroyOnHidden: true,
              children:
                detailTab === 'pillar-2-score' ? (
                  <PillarTwoComponentTabs
                    hasSelectedAttempt={Boolean(selectedUserAttempt)}
                    instrumentLimitFingerprintExists={
                      selectedUserAttempt?.has_instrument_limit_fingerprint
                    }
                    optionChoice={
                      <OverallPortfolioFitWorkspace
                        evidence={candidatePositionEvidence}
                        error={candidatePositionsError}
                        fetched={candidatePositionsFetched}
                        fetching={candidatePositionsFetching}
                        hasSelectedAttempt={Boolean(selectedUserAttempt)}
                        metric="option-choice"
                        onLoad={loadCandidatePositions}
                      />
                    }
                    portfolioFit={
                      <OverallPortfolioFitWorkspace
                        evidence={candidatePositionEvidence}
                        error={candidatePositionsError}
                        fetched={candidatePositionsFetched}
                        fetching={candidatePositionsFetching}
                        hasSelectedAttempt={Boolean(selectedUserAttempt)}
                        onLoad={loadCandidatePositions}
                      />
                    }
                    positionDirection={
                      <OverallPortfolioFitWorkspace
                        evidence={candidatePositionEvidence}
                        error={candidatePositionsError}
                        fetched={candidatePositionsFetched}
                        fetching={candidatePositionsFetching}
                        hasSelectedAttempt={Boolean(selectedUserAttempt)}
                        metric="position-direction"
                        onLoad={loadCandidatePositions}
                      />
                    }
                    positionSize={
                      <OverallPortfolioFitWorkspace
                        evidence={candidatePositionEvidence}
                        error={candidatePositionsError}
                        fetched={candidatePositionsFetched}
                        fetching={candidatePositionsFetching}
                        hasSelectedAttempt={Boolean(selectedUserAttempt)}
                        metric="position-size"
                        onLoad={loadCandidatePositions}
                      />
                    }
                    riskLimits={
                      <InstrumentRiskLimitsTable
                        error={optimalPortfolioError}
                        fetched={optimalPortfolioFetched}
                        fetching={optimalPortfolioFetching}
                        hasSelectedAttempt={Boolean(selectedUserAttempt)}
                        onLoad={loadOptimalPortfolio}
                        portfolio={optimalPortfolio}
                      />
                    }
                    optimalPortfolio={
                      <OptimalPortfolioWorkspace
                        error={optimalPortfolioError}
                        fetched={optimalPortfolioFetched}
                        fetching={optimalPortfolioFetching}
                        onLoad={loadOptimalPortfolio}
                        onSelectInstrument={instrumentId =>
                          dispatch(
                            selectInspectAssessmentInstrument(instrumentId),
                          )
                        }
                        portfolio={optimalPortfolio}
                        state={state}
                      />
                    }
                  />
                ) : null,
            },
            {
              key: 'pillar-3-score',
              label: 'Pillar 3 scoring (risk/ptf. construction)',
              destroyOnHidden: true,
              children:
                detailTab === 'pillar-3-score' ? (
                  <PillarThreeComponentTabs />
                ) : null,
            },
          ]}
          onChange={key => setDetailTab(key as AssessmentDetailTab)}
          tabBarExtraContent={
            <div className="assessment-inspect-assessment__view-selector">
              <Typography.Text
                className="assessment-inspect-assessment__view-selector-label"
                type="secondary"
              >
                View:
              </Typography.Text>
              <Segmented
                aria-label="Assessment workspace view"
                onChange={value =>
                  setWorkspaceView(value as AssessmentWorkspaceView)
                }
                options={[
                  { label: 'Per-period', value: 'per-period' },
                  { label: 'Full', value: 'full' },
                ]}
                size="small"
                value={workspaceView}
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default InspectAssessment;
